import React from "react";

/* ============================================================
   DNH Admin — shared atoms.
   ============================================================ */

export const SectionLabel = ({ children }) => (
  <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--muted)]">
    {children}
  </div>
);

export const FlagBadge = ({ flag }) => {
  const map = {
    ready: { label: "Ready for review", color: "var(--crimson)", dot: "●" },
    contradiction: { label: "Contradiction", color: "var(--ink)", dot: "◐" },
    developing: { label: "Developing", color: "var(--muted)", dot: "○" },
  };
  const m = map[flag] || map.ready;
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.08em] uppercase whitespace-nowrap">
      <span style={{ color: m.color }} className="text-[12px] leading-none">{m.dot}</span>
      <span className="text-[var(--ink-2)]">{m.label}</span>
    </span>
  );
};

export const ConfidencePill = ({ level }) => {
  const map = {
    High: { color: "var(--crimson)" },
    Moderate: { color: "var(--ink)" },
    Developing: { color: "var(--muted)" },
  };
  const m = map[level] || map.Developing;
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.1em] whitespace-nowrap"
      style={{ color: m.color }}
      data-testid={`conf-pill-${level.toLowerCase()}`}
    >
      <span aria-hidden>●</span>
      {level}
    </span>
  );
};

export const StatusDot = ({ status }) => {
  const map = {
    Healthy: { color: "var(--muted)", label: "Healthy" },
    Degraded: { color: "var(--ink)", label: "Degraded" },
    Down: { color: "var(--crimson)", label: "Down" },
    active: { color: "var(--crimson)", label: "Active" },
    invited: { color: "var(--ink)", label: "Invited" },
    suspended: { color: "var(--muted)", label: "Suspended" },
    Idle: { color: "var(--muted)", label: "Idle" },
  };
  const m = map[status] || map.Healthy;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      <span className="font-mono text-[10.5px] uppercase tracking-[0.1em]" style={{ color: m.color }}>
        {m.label}
      </span>
    </span>
  );
};

export const RoleBadge = ({ role }) => {
  const primary = role === "Editor-in-Chief" || role === "Methodology Lead";
  return (
    <span
      className={`inline-flex items-center h-6 px-2.5 rounded-full font-mono text-[10px] tracking-[0.08em] uppercase whitespace-nowrap border ${
        primary
          ? "border-[var(--crimson)] text-[var(--crimson)] bg-[var(--crimson)]/5"
          : "border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink-2)] bg-transparent"
      }`}
    >
      {role}
    </span>
  );
};

export const ScopeChip = ({ children, tone = "ink" }) => (
  <span
    className={`inline-flex items-center h-6 px-2 rounded-[6px] border font-mono text-[10px] tracking-[0.06em] uppercase whitespace-nowrap ${
      tone === "crimson"
        ? "border-[var(--crimson)] text-[var(--crimson)] bg-[var(--crimson)]/5"
        : "border-[var(--hairline)] text-[var(--ink-2)] bg-[var(--surface-2)]"
    }`}
  >
    {children}
  </span>
);

export const MiniSpark = ({ values = [], color = "var(--crimson)" }) => {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 80;
  const h = 22;
  const step = w / (values.length - 1 || 1);
  const points = values
    .map((v, i) => `${i * step},${h - ((v - min) / range) * h}`)
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
};

export const Avatar = ({ initials, size = 28 }) => (
  <span
    className="rounded-full bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center font-mono tracking-[0.06em] shrink-0"
    style={{ width: size, height: size, fontSize: size * 0.35 }}
  >
    {initials}
  </span>
);

export const FilterChip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center h-7 px-3 rounded-full font-mono text-[11px] tracking-[0.06em] uppercase whitespace-nowrap transition-colors border ${
      active
        ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
        : "bg-transparent text-[var(--ink-2)] border-[var(--hairline-strong,#D8D2C6)] hover:text-[var(--crimson)] hover:border-[var(--crimson)]"
    }`}
  >
    {children}
  </button>
);

export const Tag = ({ children }) => (
  <span className="inline-flex items-center h-5 px-1.5 rounded-[4px] bg-[var(--surface-2)] border border-[var(--hairline)] font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--muted)]">
    {children}
  </span>
);
